import { useMutation, useQueryClient } from "@tanstack/react-query";
import { userKeys } from "@/queries/keys";
import {
  saveProviderKey,
  removeProviderKey,
  updateProfile,
  updatePicture,
  changePassword,
  updateNotificationPreferences,
  updateAppearance,
  updatePreferences,
  updateVocabulary,
  updateMenuPreferences,
  updateMediaInsightPreferences,
  updateCompanyNavDefaults,
  updatePlatformLanguage,
  updateCustomization,
  updateMetaImage,
  removeMetaImage,
  requestDataExport,
  requestAccountDeletion,
  updateDeleteDataDuration,
  disconnectAccount,
  createAiContext,
  updateAiContext,
  deleteAiContext,
} from "./api";
import type {
  AIProviderName,
  UpdateProfileData,
  ChangePasswordData,
  NotificationPreferenceChange,
  CompanyPreferencesUpdate,
  AppearancePreferences,
  VocabularyUpdateData,
  MetaData,
  ImageSelectionType,
  AccountDeletionAnswer,
  MenuPreferencesUpdate,
  MediaInsightPreferencesUpdate,
  CompanyNavDefaultsUpdate,
  AiContextData,
} from "./api";
import type { SideMenuPreferences } from "@/components/layout/menu-preferences";
import type { MediaInsightPanelPreferences } from "@/features/media-insight/media-insight-preferences";

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileData) => updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.me() });
      queryClient.invalidateQueries({ queryKey: userKeys.profile() });
    },
  });
}

export function useUpdatePicture() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (formData: FormData) => updatePicture(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (data: ChangePasswordData) => changePassword(data),
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (changes: NotificationPreferenceChange[]) => {
      const res = await updateNotificationPreferences(changes);
      if (!res.success) throw Object.assign(new Error(res.error ?? "Failed to update notification preferences"), { status: res.status });
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
  });
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CompanyPreferencesUpdate) => updatePreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.preferences() });
    },
  });
}

export function useUpdateMenuPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: MenuPreferencesUpdate) => {
      const res = await updateMenuPreferences(data);
      if (!res.success) {
        throw new Error(res.error ?? "Failed to update menu preferences");
      }
      return res;
    },
    onMutate: async (data: MenuPreferencesUpdate) => {
      await queryClient.cancelQueries({ queryKey: userKeys.me() });
      const previous = queryClient.getQueryData(userKeys.me());
      queryClient.setQueryData(userKeys.me(), (old) => {
        if (!old || typeof old !== "object") return old;
        const prev = old as {
          preferences?: { sideMenu?: SideMenuPreferences } & Record<string, unknown>;
        };
        const nextSideMenu: SideMenuPreferences = {
          ...(prev.preferences?.sideMenu ?? {}),
          ...(data.order !== undefined ? { order: data.order } : {}),
          ...(data.hidden !== undefined ? { hidden: data.hidden } : {}),
          ...(data.sectionOrder !== undefined ? { sectionOrder: data.sectionOrder } : {}),
          ...(data.sectionHidden !== undefined ? { sectionHidden: data.sectionHidden } : {}),
        };
        return {
          ...prev,
          preferences: { ...prev.preferences, sideMenu: nextSideMenu },
        };
      });
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context && "previous" in context && context.previous !== undefined) {
        queryClient.setQueryData(userKeys.me(), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
  });
}

export function useUpdateMediaInsightPrefs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: MediaInsightPreferencesUpdate) => {
      const res = await updateMediaInsightPreferences(data);
      if (!res.success) {
        throw new Error(res.error ?? "Failed to update media insight preferences");
      }
      return res;
    },
    onMutate: async (data: MediaInsightPreferencesUpdate) => {
      await queryClient.cancelQueries({ queryKey: userKeys.me() });
      const previous = queryClient.getQueryData(userKeys.me());
      queryClient.setQueryData(userKeys.me(), (old) => {
        if (!old || typeof old !== "object") return old;
        const prev = old as {
          preferences?: { mediaInsightPanel?: MediaInsightPanelPreferences } & Record<string, unknown>;
        };
        const nextPanel: MediaInsightPanelPreferences = {
          ...(prev.preferences?.mediaInsightPanel ?? {}),
          ...(data.preset !== undefined ? { preset: data.preset as MediaInsightPanelPreferences["preset"] } : {}),
          ...(data.defaultTab !== undefined ? { defaultTab: data.defaultTab } : {}),
          ...(data.panelOpenByDefault !== undefined ? { panelOpenByDefault: data.panelOpenByDefault } : {}),
          ...(data.splitRatio !== undefined ? { splitRatio: data.splitRatio } : {}),
          ...(data.showVideoByDefault !== undefined ? { showVideoByDefault: data.showVideoByDefault } : {}),
          ...(data.chatOpenByDefault !== undefined ? { chatOpenByDefault: data.chatOpenByDefault } : {}),
          ...(data.tabOrder !== undefined ? { tabOrder: data.tabOrder } : {}),
          ...(data.hiddenTabs !== undefined ? { hiddenTabs: data.hiddenTabs } : {}),
          ...(data.labelsVisible !== undefined ? { labelsVisible: data.labelsVisible } : {}),
          ...(data.hiddenLabelIds !== undefined ? { hiddenLabelIds: data.hiddenLabelIds } : {}),
        };
        return {
          ...prev,
          preferences: { ...prev.preferences, mediaInsightPanel: nextPanel },
        };
      });
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context && "previous" in context && context.previous !== undefined) {
        queryClient.setQueryData(userKeys.me(), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
  });
}

export function useSetCompanyNavDefaults() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CompanyNavDefaultsUpdate) => {
      const res = await updateCompanyNavDefaults(data);
      if (!res.success) {
        throw new Error(res.error ?? "Failed to update team defaults");
      }
      return res;
    },
    onMutate: async (data: CompanyNavDefaultsUpdate) => {
      await queryClient.cancelQueries({ queryKey: userKeys.me() });
      const previous = queryClient.getQueryData(userKeys.me());
      queryClient.setQueryData(userKeys.me(), (old) => {
        if (!old || typeof old !== "object") return old;
        const prev = old as {
          companyNavDefaults?: {
            sideMenu?: SideMenuPreferences;
            mediaInsightPanel?: MediaInsightPanelPreferences;
          };
        } & Record<string, unknown>;
        const nextDefaults = {
          ...(prev.companyNavDefaults ?? {}),
          ...(data.sideMenu !== undefined
            ? { sideMenu: { ...(prev.companyNavDefaults?.sideMenu ?? {}), ...data.sideMenu } }
            : {}),
          ...(data.mediaInsightPanel !== undefined
            ? {
                mediaInsightPanel: {
                  ...(prev.companyNavDefaults?.mediaInsightPanel ?? {}),
                  ...data.mediaInsightPanel,
                } as MediaInsightPanelPreferences,
              }
            : {}),
        };
        return { ...prev, companyNavDefaults: nextDefaults };
      });
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context && "previous" in context && context.previous !== undefined) {
        queryClient.setQueryData(userKeys.me(), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
  });
}

export function useUpdateVocabulary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: VocabularyUpdateData) => updateVocabulary(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.preferences() });
    },
  });
}

export function useUpdatePlatformLanguage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (language: string) => updatePlatformLanguage(language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
  });
}

export function useUpdateAppearance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AppearancePreferences) => {
      const res = await updateAppearance(data);
      if (!res.success) {
        throw new Error(res.error ?? "Failed to save appearance");
      }
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.me() });
      queryClient.invalidateQueries({ queryKey: userKeys.profile() });
    },
  });
}

export function useUpdateCustomization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MetaData) => updateCustomization(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.customization() });
    },
  });
}

export function useUpdateMetaImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      type,
      formData,
    }: {
      type: ImageSelectionType;
      formData: FormData;
    }) => updateMetaImage(type, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.customization() });
    },
  });
}

export function useRemoveMetaImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (type: ImageSelectionType) => removeMetaImage(type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.customization() });
    },
  });
}

export function useRequestDataExport() {
  return useMutation({
    mutationFn: (feedback: string) => requestDataExport(feedback),
  });
}

export function useRequestAccountDeletion() {
  return useMutation({
    mutationFn: ({
      reason,
      questions,
    }: {
      reason: string;
      questions: AccountDeletionAnswer[];
    }) => requestAccountDeletion(reason, questions),
  });
}

export function useUpdateDeleteDataDuration() {
  return useMutation({
    mutationFn: (data: { duration: number }) => updateDeleteDataDuration(data),
  });
}


export function useCreateAiContext() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: AiContextData) => {
      const res = await createAiContext(data);
      if (!res.success)
        throw new Error(res.error ?? "Failed to create template");
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assistant-templates"] });
    },
  });
}

export function useUpdateAiContext() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: AiContextData }) => {
      const res = await updateAiContext(id, data);
      if (!res.success)
        throw new Error(res.error ?? "Failed to update template");
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assistant-templates"] });
    },
  });
}

export function useDeleteAiContext() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await deleteAiContext(id);
      if (!res.success)
        throw new Error(res.error ?? "Failed to delete template");
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assistant-templates"] });
    },
  });
}

export function useDisconnectAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (type: string) => disconnectAccount(type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.linkedAccounts() });
    },
  });
}

export function useSaveProviderKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      provider,
      key,
      label,
    }: {
      provider: AIProviderName;
      key: string;
      label?: string;
    }) => saveProviderKey(provider, { key, label }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.providerKeys() });
    },
  });
}

export function useRemoveProviderKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (provider: AIProviderName) => removeProviderKey(provider),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.providerKeys() });
    },
  });
}
